import { Modal } from "antd";
import { useEffect, useRef } from "react";

import { faTurnDownLeft } from "@fortawesome/pro-solid-svg-icons";

import OutlinedButton from "./Buttons/OutlinedButton";
import PrimaryButton from "./Buttons/PrimaryButton";

type Props = {
  isOpen: boolean;
  onDismiss: (val: boolean) => void;
  heading?: string;
  subheading?: string;
  children?: React.ReactNode;
  confirmButtonLabel?: string;
  onConfirm: () => void;
  cancelButtonLabel?: string;
  onCancel: () => void;
  loading?: boolean;
  width?: number;
};

export default function ConfirmModal({
  isOpen,
  onDismiss,
  heading,
  subheading,
  children,
  onCancel,
  onConfirm,
  loading,
  cancelButtonLabel = "Cancel",
  confirmButtonLabel = "Confirm",
  width = 400,
}: Props) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        onConfirm();
        onDismiss(true);
      }
    };

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onConfirm, onDismiss]);

  const cancelModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onDismiss(false);
  };

  const handleClickCancelModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCancel();
  };

  return (
    <Modal
      centered
      footer={null}
      closeIcon={null}
      open={isOpen}
      onCancel={cancelModal}
      width={width}
      className="[&>div>.ant-modal-content]:!p-0 [&>div>.ant-modal-content]:overflow-hidden [&>div>.ant-modal-content>.ant-modal-body]:rounded-md [&>div>.ant-modal-content]:!shadow-lg"
    >
      <div className="p-3 pb-0 text-darker font-bold">{heading}</div>
      {subheading && (
        <div className="text-xs text-gray-500 pl-3 pb-0">{subheading}</div>
      )}
      <div className="p-3 text-sm">{children}</div>
      <div className="flex items-center justify-end gap-2 p-3">
        <OutlinedButton
          size="small"
          onClick={handleClickCancelModal}
          shortcutIcons={
            <kbd className="text-xs border border-core-silver-3 px-0.5 rounded-base">
              ESC
            </kbd>
          }
        >
          {cancelButtonLabel}
        </OutlinedButton>

        <PrimaryButton
          ref={confirmButtonRef}
          loading={loading}
          type="primary"
          size="small"
          onClick={e => {
            e.stopPropagation();
            onConfirm();
            onDismiss(true);
          }}
          shortcutIcons={[faTurnDownLeft]}
        >
          {confirmButtonLabel}
        </PrimaryButton>
      </div>
    </Modal>
  );
}
